class ProductSectionController {
  constructor(section) {
    this.section = section;
    this.product = this.parseProduct();
    this.form = section.querySelector('[data-product-form]');
    this.gallery = section.querySelector('[data-product-gallery]');
    this.viewport = section.querySelector('[data-gallery-viewport]');
    this.stickyBar = section.querySelector('[data-sticky-bar]');
    this.optionButtons = [...section.querySelectorAll('[data-option-button]')];
    this.optionSelects = [...section.querySelectorAll('[data-option-select]')];
    this.variantIdInputs = [...section.querySelectorAll('[data-variant-id-input]')];
    this.quantityInput = section.querySelector('[data-quantity-input]');
    this.bundleOffer = section.querySelector('[data-bundle-offer]');
    this.bundleTiers = [];
    this.accordionTriggers = [...section.querySelectorAll('[data-accordion-trigger]')];
    this.price = section.querySelector('[data-product-price]');
    this.comparePrice = section.querySelector('[data-product-compare-price]');
    this.savingsBadge = section.querySelector('[data-product-savings-badge]');
    this.priceWrap = section.querySelector('[data-savings-format]');
    this.savingsFormat = this.priceWrap?.dataset.savingsFormat || 'percent';
    this.savingsTemplatePercent = this.priceWrap?.dataset.savingsTemplatePercent || 'Save [[value]] today';
    this.savingsTemplateAmount = this.priceWrap?.dataset.savingsTemplateAmount || 'Save [[value]] today';
    this.inventory = section.querySelector('[data-product-inventory]');
    this.sku = section.querySelector('[data-product-sku]');
    this.stickyPrice = section.querySelector('[data-sticky-price]');
    this.addToCart = section.querySelector('[data-add-to-cart]');
    this.addToCartLabel = section.querySelector('[data-add-to-cart-label]');
    this.stickySubmit = section.querySelector('[data-sticky-submit]');
    this.addToCartText = section.dataset.addToCartLabel || 'Add to cart';
    this.soldOutText = section.dataset.soldOutLabel || 'Sold out';
    this.unavailableText = section.dataset.unavailableLabel || 'Unavailable';
    this.availableToOrderText = section.dataset.availableToOrderLabel || 'Available to order';
    this.inStockTemplate = section.dataset.inStockTemplate || '[[count]] in stock';
    this.skuTemplate = section.dataset.skuTemplate || 'SKU: [[value]]';
    this.currentVariant = this.findInitialVariant();
    // The gallery opens on the first media photo (see product-gallery.liquid), not on
    // the variant's photo. Remember the variant's media up front so the first
    // updateVariantUI leaves the gallery alone and only a real colour switch moves it.
    this.variantMediaId = this.currentVariant?.featured_media?.id;

    this.randomizeLowStockCount();

    if (!this.product || !this.form) return;

    this.bindOptionInputs();
    this.bindQuantityControls();
    this.bindBundleOffer();
    this.bindGallery();
    this.setupPeekLoop();
    this.bindStickyBar();
    this.bindAccordions();
    this.applySelections(this.currentVariant?.options || this.getSelectedOptions());
    this.updateVariantUI(this.currentVariant);
    this.showInitialMedia();
  }

  showInitialMedia() {
    // Bring the photo rendered as active into view (a ?variant= link starts on that
    // variant's photo, which is not the first one on desktop).
    const active = this.viewport?.querySelector('.product-gallery__item.is-active');
    if (active) this.showMedia(Number(active.dataset.mediaId));
  }

  parseProduct() {
    const node = this.section.querySelector('[data-product-json]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Unable to parse product JSON', error);
      return null;
    }
  }

  findInitialVariant() {
    const currentId = Number(this.form.querySelector('[name="id"]')?.value);
    return (
      this.product.variants.find((variant) => variant.id === currentId) ||
      this.product.selected_or_first_available_variant ||
      this.product.variants[0]
    );
  }

  bindOptionInputs() {
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        const optionName = button.dataset.optionName;
        const optionValue = button.dataset.optionValue;
        this.handleVariantChange(optionName, optionValue);
      });
    });

    this.optionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        this.handleVariantChange(select.dataset.optionName, select.value);
      });
    });
  }

  updateSelectedValue(optionName, optionValue) {
    const option = this.product.options.findIndex((name) => name === optionName);
    const label = this.section.querySelector(`[data-selected-option="${option + 1}"]`);
    if (label) label.textContent = optionValue;
  }

  getSelectedOptions() {
    return this.product.options.map((optionName) => {
      const select = this.optionSelects.find((item) => item.dataset.optionName === optionName);
      if (select) return select.value;

      const button = this.optionButtons.find(
        (item) => item.dataset.optionName === optionName && item.classList.contains('is-active')
      );
      return button?.dataset.optionValue;
    });
  }

  handleVariantChange(optionName, optionValue) {
    const optionIndex = this.product.options.findIndex((name) => name === optionName);
    const selectedOptions = this.getSelectedOptions();

    if (optionIndex >= 0) {
      selectedOptions[optionIndex] = optionValue;
    }

    const nextVariant = this.resolveVariantFromSelection(selectedOptions, optionIndex);
    if (!nextVariant) return;

    const normalizedSelections = [...nextVariant.options];
    this.applySelections(normalizedSelections);
    this.currentVariant = nextVariant;
    this.updateVariantUI(nextVariant);
    this.updateVariantUrl(nextVariant);
  }

  updateVariantUrl(variant) {
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(variant.id)) return;

    url.searchParams.set('variant', variant.id);
    // Tracking scripts (Clarity, Klaviyo, ad pixels) wrap history.replaceState and treat
    // every call as a new page view, doing heavy work inside the tap. A variant switch is
    // not a new page, so call the browser's own method and skip those wrappers.
    History.prototype.replaceState.call(window.history, { variantId: variant.id }, '', url.href);
  }

  applySelections(selectedOptions) {
    this.product.options.forEach((optionName, index) => {
      const selectedValue = selectedOptions[index];
      const relatedButtons = this.optionButtons.filter((button) => button.dataset.optionName === optionName);

      relatedButtons.forEach((button) => {
        const isActive = button.dataset.optionValue === selectedValue;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      const select = this.optionSelects.find((item) => item.dataset.optionName === optionName);
      if (select) select.value = selectedValue;

      this.updateSelectedValue(optionName, selectedValue);
    });

    this.updateOptionAvailability(selectedOptions);
  }

  updateOptionAvailability(selectedOptions) {
    this.product.options.forEach((optionName, index) => {
      this.optionButtons
        .filter((button) => button.dataset.optionName === optionName)
        .forEach((button) => {
          const isReachable = this.isOptionValueReachable(index, button.dataset.optionValue, selectedOptions);
          button.disabled = !isReachable;
          button.classList.toggle('is-unavailable', !isReachable);
          button.setAttribute('aria-disabled', isReachable ? 'false' : 'true');
          if (!isReachable) {
            button.title = button.dataset.unavailableLabel || 'Sold out';
          } else {
            button.removeAttribute('title');
          }
        });

      const select = this.optionSelects.find((item) => item.dataset.optionName === optionName);
      if (!select) return;

      [...select.options].forEach((option) => {
        option.disabled = !this.isOptionValueReachable(index, option.value, selectedOptions);
      });
    });
  }

  isOptionValueReachable(index, optionValue, selectedOptions) {
    return this.product.variants.some((variant) =>
      variant.options.every((value, variantIndex) => {
        if (variantIndex === index) return value === optionValue;

        const selectedValue = selectedOptions[variantIndex];
        return !selectedValue || value === selectedValue;
      })
    );
  }

  resolveVariantFromSelection(selectedOptions, changedIndex = 0) {
    const exactVariant = this.findVariantByOptions(selectedOptions);
    if (exactVariant) return exactVariant;

    const rankedVariants = this.product.variants
      .filter((variant) => {
        if (changedIndex < 0) return true;
        return variant.options[changedIndex] === selectedOptions[changedIndex];
      })
      .map((variant) => ({
        variant,
        score: variant.options.reduce((total, value, index) => total + (value === selectedOptions[index] ? 1 : 0), 0)
      }))
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        if (left.variant.available !== right.variant.available) {
          return Number(right.variant.available) - Number(left.variant.available);
        }

        return 0;
      });

    return rankedVariants[0]?.variant || null;
  }

  findVariantByOptions(selectedOptions) {
    return this.product.variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );
  }

  updateVariantUI(variant) {
    this.variantIdInputs.forEach((input) => {
      input.value = variant.id;
    });

    if (this.price) this.price.textContent = this.formatMoney(variant.price);
    if (this.stickyPrice) this.stickyPrice.textContent = this.formatMoney(variant.price);
    this.updateBundlePrices(variant);

    if (this.comparePrice) {
      if (variant.compare_at_price > variant.price) {
        this.comparePrice.hidden = false;
        this.comparePrice.textContent = this.formatMoney(variant.compare_at_price);
      } else {
        this.comparePrice.hidden = true;
        this.comparePrice.textContent = '';
      }
    }

    if (this.savingsBadge) {
      if (variant.compare_at_price > variant.price) {
        const savingsAmount = variant.compare_at_price - variant.price;
        const savingsPercent = Math.round((savingsAmount / variant.compare_at_price) * 100);
        this.savingsBadge.hidden = false;
        this.savingsBadge.textContent =
          this.savingsFormat === 'amount'
            ? this.savingsTemplateAmount.replace('[[value]]', this.formatMoney(savingsAmount))
            : this.savingsTemplatePercent.replace('[[value]]', `${savingsPercent}%`);
      } else {
        this.savingsBadge.hidden = true;
        this.savingsBadge.textContent = '';
      }
    }

    if (this.inventory) {
      this.inventory.textContent = variant.available
        ? variant.inventory_quantity > 0
          ? this.inStockTemplate.replace('[[count]]', variant.inventory_quantity)
          : this.availableToOrderText
        : this.unavailableText;
    }

    if (this.sku) {
      this.sku.textContent = variant.sku ? this.skuTemplate.replace('[[value]]', variant.sku) : '';
    }

    if (this.addToCart && this.addToCartLabel) {
      this.addToCart.disabled = !variant.available;
      this.addToCartLabel.textContent = variant.available ? this.addToCartText : this.soldOutText;
    }

    // Only move the gallery when the variant's image changes, so a size tap leaves it alone
    const mediaId = variant.featured_media?.id;
    if (mediaId && mediaId !== this.variantMediaId) {
      this.variantMediaId = mediaId;
      this.showMedia(mediaId);
    }
  }

  bindGallery() {
    if (!this.gallery || !this.viewport) return;

    const thumbButtons = [...this.section.querySelectorAll('[data-gallery-thumb]')];
    const items = [...this.section.querySelectorAll('[data-media-id]')];
    const prev = this.section.querySelector('[data-gallery-prev]');
    const next = this.section.querySelector('[data-gallery-next]');
    const modal = this.section.querySelector('[data-gallery-modal]');
    const modalBody = this.section.querySelector('[data-gallery-modal-body]');
    const modalThumbs = this.section.querySelector('[data-gallery-modal-thumbs]');
    const modalTemplates = [...this.section.querySelectorAll('[data-gallery-modal-template]')];
    const closeModal = this.section.querySelector('[data-gallery-close]');
    const mediaIds = items.map((item) => Number(item.dataset.mediaId));
    let lastZoomTrigger = null;
    let activeModalMediaId = null;

    thumbButtons.forEach((button) => {
      button.addEventListener('click', () => this.showMedia(Number(button.dataset.targetMediaId)));
    });

    // Arrows wrap around: on the last photo "→" used to do nothing and Clarity
    // logged rage clicks on it. arrowIndex remembers the target while the smooth
    // scroll is still running, so quick repeat clicks each advance one photo.
    let arrowIndex = null;
    let arrowTimer = null;
    const step = (direction) => {
      if (!items.length) return;
      const from = arrowIndex ?? this.getCenteredIndex(items);
      arrowIndex = (from + direction + items.length) % items.length;
      this.showMedia(mediaIds[arrowIndex]);
      window.clearTimeout(arrowTimer);
      arrowTimer = window.setTimeout(() => {
        arrowIndex = null;
      }, 700);
    };

    prev?.addEventListener('click', () => step(-1));
    next?.addEventListener('click', () => step(1));

    // Zoom view: every photo is a slide in a native scroll-snap track, so a swipe
    // follows the finger and the next photo is already loaded (re-rendering one
    // image per swipe felt slow). On a phone a photo fills the height between the
    // top and the thumbnail row (the square photos only got the screen width).
    // Tap/click zooms in at that spot and again zooms out, two fingers pinch (out
    // to the whole photo), a drag pans a zoomed photo, and the thumbnail row
    // follows the photo on screen.
    const ZOOM_TAP = 2.5;
    const ZOOM_MAX = 4;
    const zoom = { img: null, scale: 1, x: 0, y: 0 };
    const pointers = new Map();
    let gesture = null;
    let track = null;
    let slides = [];
    let activeIndex = -1;

    const slideImage = (index) => slides[index]?.querySelector('.product-gallery__modal-image') || null;

    // Centre of the image's untransformed box: its wrapper fills the slide and centres it
    const imageCenter = (img) => {
      const rect = img.parentElement.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    };

    // Smallest scale shows the whole photo; on a phone the base size crops its sides
    const minScale = (img) => {
      const box = img.parentElement.getBoundingClientRect();
      return Math.min(1, box.width / img.offsetWidth, box.height / img.offsetHeight);
    };

    const setZoom = (img, scale, x, y, animate) => {
      const box = img.parentElement.getBoundingClientRect();
      const maxX = Math.max(0, (img.offsetWidth * scale - box.width) / 2);
      const maxY = Math.max(0, (img.offsetHeight * scale - box.height) / 2);
      zoom.img = img;
      zoom.scale = scale;
      zoom.x = scale === 1 ? 0 : Math.max(-maxX, Math.min(maxX, x));
      zoom.y = scale === 1 ? 0 : Math.max(-maxY, Math.min(maxY, y));
      img.classList.toggle('is-animating', animate);
      img.classList.toggle('is-zoomed', scale > 1);
      img.style.transform = scale === 1 ? '' : `translate(${zoom.x}px, ${zoom.y}px) scale(${scale})`;
      // While zoomed, one-finger drags pan the photo instead of swiping the track
      track?.classList.toggle('is-zoomed', scale > 1.01);
    };

    // Zoom to `scale`, keeping the photo point under (px, py) where it is
    const zoomAt = (img, scale, px, py, animate) => {
      const center = imageCenter(img);
      const ratio = scale / zoom.scale;
      setZoom(
        img,
        scale,
        px - center.x - ratio * (px - center.x - zoom.x),
        py - center.y - ratio * (py - center.y - zoom.y),
        animate
      );
    };

    const resetZoom = () => {
      if (zoom.img) setZoom(zoom.img, 1, 0, 0, false);
      zoom.img = null;
    };

    const setActive = (index) => {
      if (index === activeIndex || !slides[index]) return;
      if (zoom.img && zoom.img !== slideImage(index)) resetZoom();
      activeIndex = index;
      activeModalMediaId = mediaIds[index];

      // Load the photo on screen and its neighbours; the rest stay lazy
      [index - 1, index, index + 1].forEach((i) => {
        const img = slideImage((i + slides.length) % slides.length);
        if (img) img.loading = 'eager';
      });

      modalThumbs?.querySelectorAll('[data-gallery-modal-thumb]').forEach((thumb) => {
        const isActive = Number(thumb.dataset.targetMediaId) === activeModalMediaId;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        if (!isActive || modalThumbs.scrollWidth <= modalThumbs.clientWidth) return;
        // Keep the active thumbnail in view: the row didn't follow the swipes
        const left =
          thumb.getBoundingClientRect().left - modalThumbs.getBoundingClientRect().left + modalThumbs.scrollLeft;
        modalThumbs.scrollTo({ left: left - (modalThumbs.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
      });
    };

    const goToSlide = (index, behavior = 'smooth') => {
      if (!track || !slides.length) return;
      const target = (index + slides.length) % slides.length;
      track.scrollTo({ left: target * track.clientWidth, behavior });
      setActive(target);
    };

    const goToAdjacentSlide = (direction) => {
      const target = activeIndex + direction;
      // Wrapping jumps; smooth-scrolling past every photo back to the first looks broken
      goToSlide(target, target < 0 || target >= slides.length ? 'auto' : 'smooth');
    };

    const onTrackScroll = () => {
      if (track?.clientWidth) setActive(Math.round(track.scrollLeft / track.clientWidth));
    };

    const buildSlides = () => {
      track = document.createElement('div');
      track.className = 'product-gallery__modal-track';
      slides = modalTemplates.map((template) => {
        const slide = document.createElement('div');
        slide.className = 'product-gallery__modal-slide';
        slide.append(template.content.cloneNode(true));
        slide.querySelectorAll('img').forEach((img) => {
          img.loading = 'lazy';
          img.draggable = false;
        });
        track.append(slide);
        return slide;
      });
      track.addEventListener('scroll', onTrackScroll, { passive: true });
      modalBody.replaceChildren(track);
      activeIndex = -1;
    };

    const startPan = (x, y) => {
      gesture = { type: 'pan', startX: x, startY: y, originX: zoom.x, originY: zoom.y, moved: false };
    };

    // Two fingers are a pinch for us, not a swipe of the track for the browser
    modalBody?.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length > 1 && event.cancelable) event.preventDefault();
      },
      { passive: false }
    );

    modalBody?.addEventListener('pointerdown', (event) => {
      const img = slideImage(activeIndex);
      if (!img || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.pointerType === 'mouse') modalBody.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      img.classList.remove('is-animating');
      zoom.img = img;

      if (pointers.size === 1) {
        startPan(event.clientX, event.clientY);
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        gesture = {
          type: 'pinch',
          distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
          midX: (a.x + b.x) / 2,
          midY: (a.y + b.y) / 2,
          scale: zoom.scale,
          x: zoom.x,
          y: zoom.y
        };
      }
    });

    modalBody?.addEventListener('pointermove', (event) => {
      const img = zoom.img;
      if (!img || !gesture || !pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (gesture.type === 'pinch' && pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const scale = Math.max(
          minScale(img),
          Math.min(ZOOM_MAX, (gesture.scale * Math.hypot(a.x - b.x, a.y - b.y)) / gesture.distance)
        );
        const center = imageCenter(img);
        // Keep the photo point that was under the fingers' midpoint under it
        const ux = (gesture.midX - center.x - gesture.x) / gesture.scale;
        const uy = (gesture.midY - center.y - gesture.y) / gesture.scale;
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        setZoom(img, scale, midX - center.x - scale * ux, midY - center.y - scale * uy, false);
      } else if (gesture.type === 'pan') {
        const dx = event.clientX - gesture.startX;
        const dy = event.clientY - gesture.startY;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) gesture.moved = true;
        if (gesture.moved && zoom.scale > 1) {
          setZoom(img, zoom.scale, gesture.originX + dx, gesture.originY + dy, false);
        }
      }
    });

    const endPointer = (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.delete(event.pointerId);
      const img = zoom.img;
      if (!img || !gesture) return;

      if (gesture.type === 'pinch') {
        if (pointers.size === 1) {
          // One finger stays down: carry on as a pan from where it is
          const [rest] = [...pointers.values()];
          startPan(rest.x, rest.y);
          gesture.moved = true;
        } else if (pointers.size === 0) {
          if (Math.abs(zoom.scale - 1) < 0.1) setZoom(img, 1, 0, 0, true);
          gesture = null;
        }
        return;
      }

      const { moved, startX, startY } = gesture;
      gesture = null;
      if (event.type === 'pointercancel') return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      // A tap that only stops a swipe half-way shouldn't zoom
      const settled = !track || Math.abs(track.scrollLeft - activeIndex * track.clientWidth) < 2;
      if (!moved && settled) {
        if (zoom.scale > 1) {
          setZoom(img, 1, 0, 0, true);
        } else {
          zoomAt(img, ZOOM_TAP, event.clientX, event.clientY, true);
        }
      } else if (
        moved &&
        event.pointerType === 'mouse' &&
        zoom.scale <= 1 &&
        Math.abs(dx) > 50 &&
        Math.abs(dx) > Math.abs(dy)
      ) {
        // A mouse can't swipe the track natively
        goToAdjacentSlide(dx < 0 ? 1 : -1);
      }
    };

    modalBody?.addEventListener('pointerup', endPointer);
    modalBody?.addEventListener('pointercancel', endPointer);
    modalBody?.addEventListener('dragstart', (event) => event.preventDefault());

    modalThumbs?.querySelectorAll('[data-gallery-modal-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        goToSlide(mediaIds.indexOf(Number(thumb.dataset.targetMediaId)), 'auto');
      });
    });

    window.addEventListener('resize', () => {
      if (modal?.open && track) track.scrollLeft = activeIndex * track.clientWidth;
    });

    const openModal = (mediaId, trigger) => {
      if (!mediaId || !modal || !modalBody) return;
      lastZoomTrigger = trigger;
      buildSlides();
      document.documentElement.classList.add('product-gallery-modal-open');
      document.body.classList.add('product-gallery-modal-open');
      modal.showModal();
      goToSlide(Math.max(0, mediaIds.indexOf(mediaId)), 'auto');
      closeModal?.focus();
    };

    // Delegated so the peek loop's cloned slides (which carry no listeners) work
    // too. Tapping a photo opens it like the zoom button: Clarity showed shoppers
    // tapping photos and nothing happening. A swipe never fires a click.
    this.viewport.addEventListener('click', (event) => {
      const item = event.target.closest('.product-gallery__item');
      if (!item || !this.viewport.contains(item)) return;

      const mediaId = Number(item.dataset.mediaId || item.dataset.cloneOf);
      const zoomButton = event.target.closest('[data-gallery-zoom]');

      if (!zoomButton) {
        if (item.dataset.mediaType !== 'image') return;
        // A half-visible side slide in the mobile peek carousel slides in first
        if (!this.isCentered(item)) {
          this.centerGalleryItem(item);
          return;
        }
      }

      openModal(mediaId, zoomButton || item.querySelector('[data-gallery-zoom]'));
    });

    closeModal?.addEventListener('click', () => modal?.close());
    modal?.addEventListener('click', (event) => {
      if (event.target === modal) modal.close();
    });
    modal?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToAdjacentSlide(-1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToAdjacentSlide(1);
      }
    });
    modal?.addEventListener('close', () => {
      // Leave the gallery on the photo last viewed in the zoom view
      const lastViewedId = activeModalMediaId;
      modalBody?.replaceChildren();
      track = null;
      slides = [];
      activeIndex = -1;
      activeModalMediaId = null;
      zoom.img = null;
      zoom.scale = 1;
      zoom.x = 0;
      zoom.y = 0;
      pointers.clear();
      gesture = null;
      document.documentElement.classList.remove('product-gallery-modal-open');
      document.body.classList.remove('product-gallery-modal-open');
      if (lastViewedId !== null) this.showMedia(lastViewedId);
      lastZoomTrigger?.focus({ preventScroll: true });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (!visible) return;
        const mediaId = Number(visible.target.dataset.mediaId);
        items.forEach((item) => item.classList.toggle('is-active', item === visible.target));
        thumbButtons.forEach((button) =>
          button.classList.toggle('is-active', Number(button.dataset.targetMediaId) === mediaId)
        );
      },
      { root: this.viewport, threshold: 0.55 }
    );

    items.forEach((item) => observer.observe(item));
  }

  setupPeekLoop() {
    const viewport = this.viewport;
    if (!viewport || !this.gallery || !this.gallery.classList.contains('product-gallery--mobile-peek')) return;

    const reals = [...viewport.children].filter((child) => child.hasAttribute('data-media-id'));
    if (reals.length < 2) return;

    const mq = window.matchMedia('(max-width: 989px)');
    let clones = [];
    let scrollTimer = null;
    let jumping = false;

    const centerOn = (el) => {
      jumping = true;
      const itemLeft = el.getBoundingClientRect().left - viewport.getBoundingClientRect().left + viewport.scrollLeft;
      viewport.scrollTo({ left: itemLeft - (viewport.clientWidth - el.offsetWidth) / 2, behavior: 'auto' });
      window.requestAnimationFrame(() => {
        jumping = false;
      });
    };

    const isCentered = (el) => {
      const vpRect = viewport.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      return Math.abs(rect.left + rect.width / 2 - (vpRect.left + vpRect.width / 2)) < rect.width / 2;
    };

    const handleSettle = () => {
      if (jumping) return;
      const clone = clones.find(isCentered);
      if (!clone) return;
      const real = reals.find((item) => item.dataset.mediaId === clone.dataset.cloneOf);
      if (real) centerOn(real);
    };

    // Jump from a clone back to its real slide the moment the swipe settles.
    // scrollend fires right after momentum + snap; the debounce is only the
    // fallback for browsers without it (its pause read as a hiccup on the last
    // photo). Whichever runs second finds no clone centred and does nothing.
    const onScroll = () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(handleSettle, 150);
    };

    const makeClone = (item) => {
      const clone = item.cloneNode(true);
      clone.dataset.cloneOf = clone.dataset.mediaId;
      clone.removeAttribute('data-media-id');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      // Load with the page so a clone never shows up blank mid-swipe
      clone.querySelectorAll('img').forEach((img) => {
        img.loading = 'eager';
        img.setAttribute('fetchpriority', 'low');
      });
      return clone;
    };

    const enable = () => {
      if (clones.length) return;
      // Two clones per side, so the peeking neighbour slivers are there on a
      // clone too; with one, the right edge was empty on the last-photo clone.
      const lead = reals.slice(-2).map(makeClone);
      const tail = reals.slice(0, 2).map(makeClone);
      lead.forEach((clone) => viewport.insertBefore(clone, reals[0]));
      tail.forEach((clone) => viewport.appendChild(clone));
      clones = [...lead, ...tail];
      viewport.addEventListener('scrollend', handleSettle);
      viewport.addEventListener('scroll', onScroll, { passive: true });
      // Start on the selected variant's photo (rendered as .is-active), not the
      // first one: this jump used to cancel the scroll to it, so a Google Shopping
      // click on the green variant opened on a red photo.
      const start = reals.find((item) => item.classList.contains('is-active')) || reals[0];
      window.requestAnimationFrame(() => centerOn(start));
    };

    const disable = () => {
      if (!clones.length) return;
      viewport.removeEventListener('scrollend', handleSettle);
      viewport.removeEventListener('scroll', onScroll);
      window.clearTimeout(scrollTimer);
      clones.forEach((clone) => clone.remove());
      clones = [];
    };

    const apply = () => {
      if (mq.matches) {
        enable();
      } else {
        disable();
      }
    };

    apply();
    mq.addEventListener('change', apply);
  }

  bindQuantityControls() {
    if (!this.quantityInput) return;

    const decrease = this.section.querySelector('[data-quantity-decrease]');
    const increase = this.section.querySelector('[data-quantity-increase]');

    decrease?.addEventListener('click', () => {
      const nextValue = Math.max(1, Number(this.quantityInput.value || 1) - 1);
      this.quantityInput.value = nextValue;
      this.syncBundleTierToQuantity(nextValue);
    });

    increase?.addEventListener('click', () => {
      const nextValue = Math.max(1, Number(this.quantityInput.value || 1) + 1);
      this.quantityInput.value = nextValue;
      this.syncBundleTierToQuantity(nextValue);
    });

    this.quantityInput.addEventListener('change', () => {
      const normalized = Math.max(1, Number(this.quantityInput.value || 1));
      this.quantityInput.value = normalized;
      this.syncBundleTierToQuantity(normalized);
    });
  }

  // Bundle offer block: each tier is a quantity; picking one sets the form's quantity
  // (the hidden input the block renders, or the quantity box when that block exists).
  bindBundleOffer() {
    if (!this.bundleOffer) return;

    this.bundleTiers = [...this.bundleOffer.querySelectorAll('[data-bundle-tier]')];
    this.bundleTiers.forEach((tier) => {
      tier.addEventListener('click', () => this.selectBundleTier(tier));
    });

    const preselected =
      this.bundleTiers.find((tier) => tier.getAttribute('aria-checked') === 'true') || this.bundleTiers[0];
    if (preselected) this.selectBundleTier(preselected);

    this.startBundleCountdown();
  }

  selectBundleTier(selected) {
    this.bundleTiers.forEach((tier) => {
      const isSelected = tier === selected;
      tier.classList.toggle('is-selected', isSelected);
      tier.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    const quantity = Math.max(1, Number(selected.dataset.quantity || 1));
    this.form.querySelectorAll('[name="quantity"]').forEach((input) => {
      input.value = quantity;
    });
  }

  syncBundleTierToQuantity(quantity) {
    if (!this.bundleTiers.length) return;
    this.bundleTiers.forEach((tier) => {
      const isSelected = Number(tier.dataset.quantity) === Number(quantity);
      tier.classList.toggle('is-selected', isSelected);
      tier.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
  }

  // Tier totals follow the selected variant unless the merchant filled in a fixed price
  updateBundlePrices(variant) {
    if (!this.bundleTiers.length || !variant) return;

    this.bundleTiers.forEach((tier) => {
      const quantity = Math.max(1, Number(tier.dataset.quantity || 1));
      const discount = Math.min(100, Math.max(0, Number(tier.dataset.discount || 0)));
      const priceOverride = tier.dataset.priceOverride ? Number(tier.dataset.priceOverride) : null;
      const compareOverride = tier.dataset.compareOverride ? Number(tier.dataset.compareOverride) : null;
      const unitCompare = Math.max(variant.compare_at_price || 0, variant.price);

      const price = priceOverride ?? Math.round((variant.price * quantity * (100 - discount)) / 100);
      const compare = compareOverride ?? unitCompare * quantity;

      const priceNode = tier.querySelector('[data-bundle-price]');
      const compareNode = tier.querySelector('[data-bundle-compare]');
      if (priceNode) priceNode.textContent = this.formatMoney(price);
      if (compareNode) {
        compareNode.textContent = this.formatMoney(compare);
        compareNode.hidden = compare <= price;
      }
    });
  }

  // "[countdown]" in the heading counts down to midnight in the visitor's own time zone
  startBundleCountdown() {
    const node = this.bundleOffer?.querySelector('[data-bundle-countdown]');
    if (!node) return;

    const pad = (value) => String(value).padStart(2, '0');
    const tick = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const remaining = Math.max(0, Math.floor((midnight - now) / 1000));
      node.textContent = `${pad(Math.floor(remaining / 3600))}:${pad(Math.floor((remaining % 3600) / 60))}:${pad(remaining % 60)}`;
    };

    tick();
    window.setInterval(tick, 1000);
  }

  showMedia(mediaId) {
    const target = this.section.querySelector(`[data-media-id="${mediaId}"]`);
    if (!target || !this.viewport?.contains(target)) return;

    this.centerGalleryItem(target);

    this.section.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
      button.classList.toggle('is-active', Number(button.dataset.targetMediaId) === mediaId);
    });
  }

  centerGalleryItem(target) {
    // Scroll only the gallery itself: scrollIntoView also scrolled the page, pulling
    // mobile shoppers back up to the photos from the size buttons.
    const offset = target.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left;
    this.viewport.scrollTo({
      left: this.viewport.scrollLeft + offset - (this.viewport.clientWidth - target.offsetWidth) / 2,
      behavior: 'smooth'
    });
  }

  isCentered(item) {
    const vpRect = this.viewport.getBoundingClientRect();
    const rect = item.getBoundingClientRect();
    return Math.abs(rect.left + rect.width / 2 - (vpRect.left + vpRect.width / 2)) < rect.width / 2;
  }

  getCenteredIndex(items) {
    const vpRect = this.viewport.getBoundingClientRect();
    const center = vpRect.left + vpRect.width / 2;
    let best = 0;
    let bestDistance = Infinity;
    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  randomizeLowStockCount() {
    // Low-stock block with "show a stock count" enabled. The number is kept per
    // visitor in localStorage so a refresh shows the same value; once the hold
    // period has passed it may only stay the same or drop by a little, and
    // after a day a fresh number is picked.
    const DAY_MS = 24 * 60 * 60 * 1000;
    const randomBetween = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

    this.section.querySelectorAll('[data-low-stock-count]').forEach((node) => {
      let min = parseInt(node.dataset.countMin, 10);
      let max = parseInt(node.dataset.countMax, 10);
      if (Number.isNaN(min) || Number.isNaN(max)) return;
      if (max < min) [min, max] = [max, min];

      const holdMs = Math.max(1, parseInt(node.dataset.countHoldMinutes, 10) || 60) * 60 * 1000;
      const storageKey = `rp-low-stock-${node.dataset.countKey || 'product'}`;
      const now = Date.now();

      let stored = null;
      try {
        stored = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
      } catch (error) {
        stored = null;
      }

      let count;
      const valid =
        stored &&
        Number.isInteger(stored.count) &&
        Number.isFinite(stored.at) &&
        Number.isFinite(stored.first) &&
        stored.count >= min &&
        stored.count <= max &&
        now - stored.first < DAY_MS;

      if (valid && now - stored.at < holdMs) {
        count = stored.count;
      } else if (valid) {
        // Realistic decline: per elapsed hold period a 50% chance of one fewer,
        // at most 3 lower in one go, never below the minimum.
        const periods = Math.min(3, Math.floor((now - stored.at) / holdMs));
        let drop = 0;
        for (let i = 0; i < periods; i += 1) {
          if (Math.random() < 0.5) drop += 1;
        }
        count = Math.max(min, stored.count - drop);
        stored = { count, at: now, first: stored.first };
      } else {
        count = randomBetween(min, max);
        stored = { count, at: now, first: now };
      }

      try {
        window.localStorage.setItem(storageKey, JSON.stringify(stored));
      } catch (error) {
        // Storage unavailable (private mode etc.): the number just won't persist.
      }

      const template = node.dataset.countTemplate || 'Only [count] left in stock';
      node.textContent = template.replace('[count]', String(count));
    });
  }

  bindStickyBar() {
    if (!this.stickyBar || !this.addToCart || !this.stickySubmit) return;

    this.stickySubmit.addEventListener('click', () => {
      this.addToCart.click();
    });

    let hasSeenAddToCart = false;
    let rafId = null;

    const updateStickyBarVisibility = () => {
      const buttonBounds = this.addToCart.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const isVisible =
        buttonBounds.bottom > 0 &&
        buttonBounds.top < viewportHeight;

      if (isVisible) hasSeenAddToCart = true;

      const hasScrolledPastButton = buttonBounds.bottom <= 0;
      this.stickyBar.hidden = !(hasSeenAddToCart && hasScrolledPastButton);
      rafId = null;
    };

    const requestStickyBarUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(updateStickyBarVisibility);
    };

    updateStickyBarVisibility();

    window.addEventListener('scroll', requestStickyBarUpdate, { passive: true });
    window.addEventListener('resize', requestStickyBarUpdate);
  }

  bindAccordions() {
    if (!this.accordionTriggers.length) return;

    this.accordionTriggers.forEach((trigger) => {
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? this.section.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (!panel) return;

      if (panel.dataset.accordionOpenDefault === 'true') {
        panel.style.height = `${panel.scrollHeight}px`;
      }

      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        this.setAccordionState(trigger, panel, !isOpen);
      });
    });
  }

  setAccordionState(trigger, panel, shouldOpen) {
    trigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    trigger.classList.toggle('is-open', shouldOpen);
    panel.classList.toggle('is-open', shouldOpen);

    if (shouldOpen) {
      panel.style.height = `${panel.scrollHeight}px`;
    } else {
      panel.style.height = `${panel.scrollHeight}px`;
      requestAnimationFrame(() => {
        panel.style.height = '0px';
      });
    }
  }

  formatMoney(amount) {
    const currency = window.Shopify?.currency?.active || 'USD';
    // Euro prices always use the French notation (59,95 €), like snippets/price.liquid
    const locale = currency === 'EUR' ? 'fr-FR' : document.documentElement.lang || 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(amount / 100);
  }

}

document.querySelectorAll('[data-product-section]').forEach((section) => {
  new ProductSectionController(section);
});
